import { ObjectiveStatus, ObjectiveDto, Currency } from '../../../../types'
import { TFunction } from 'i18next'
import { PerTableActionButtons } from '../../../../components/custom_components/TableActionButtons'

export const createObjectivesActionButtons = (
  t: TFunction,
  status: ObjectiveStatus,
  objective: ObjectiveDto,
  employeeView: boolean,
  disabled: boolean,
  isDismissing: boolean,
  toggleDismissing: React.Dispatch<React.SetStateAction<boolean>>,
  isReactivating: boolean,
  toggleReactivating: React.Dispatch<React.SetStateAction<boolean>>,
  isDuplicating: boolean,
  toggleDuplicating: React.Dispatch<React.SetStateAction<boolean>>,
  handleObjectiveClicked: (isManagerPreparation: boolean, objective: ObjectiveDto) => void,
  handleObjectiveDismiss: (objectiveId: number) => Promise<void>,
  handleObjectiveReactivate: (objectiveId: number) => Promise<void>,
  handleObjectiveDuplicate: (objectiveGlobalId: string) => Promise<void>,
  canDuplicate: boolean,
  isManagerPreparation?: boolean,
  employeeCurrentContractCurrency?: Currency,
  handleObjectiveDelete?: (objective?: ObjectiveDto) => void,
  handleObjectiveDuplicateAndDismissOrDelete?: (objective?: ObjectiveDto) => void,
): JSX.Element => {
  const isButtonDisabled = () => {
    return isDismissing || isReactivating || isDuplicating
  }

  const canDismissObjective = (status: ObjectiveStatus) => {
    return (
      (objective.targetAmount === undefined ||
        objective.targetAmount === null ||
        !objective.currency ||
        employeeCurrentContractCurrency === objective.currency) &&
      objective.id &&
      !employeeView &&
      !disabled &&
      (status === ObjectiveStatus.Approved || status === ObjectiveStatus.MarkedAsDone)
    )
  }

  const canDeleteObjective = (status: ObjectiveStatus) => {
    return (
      (objective.targetAmount === undefined ||
        objective.targetAmount === null ||
        !objective.currency ||
        employeeCurrentContractCurrency === objective.currency) &&
      !disabled &&
      ([ObjectiveStatus.Proposal, ObjectiveStatus.ProposalFromManager, ObjectiveStatus.PastApproved].includes(status) ||
        ([ObjectiveStatus.Approved, ObjectiveStatus.MarkedAsDone].includes(status) && objective.id === null))
    )
  }

  const canReactivateObjective = (status: ObjectiveStatus) => {
    return (
      (objective.targetAmount === undefined ||
        objective.targetAmount === null ||
        !objective.currency ||
        employeeCurrentContractCurrency === objective.currency) &&
      !disabled &&
      status === ObjectiveStatus.DismissedPending
    )
  }

  const canDuplicateObjective = (status: ObjectiveStatus) => {
    return (
      (objective.targetAmount === undefined ||
        objective.targetAmount === null ||
        !objective.currency ||
        employeeCurrentContractCurrency === objective.currency) &&
      !disabled &&
      canDuplicate &&
      [ObjectiveStatus.Proposal, ObjectiveStatus.ProposalFromManager, ObjectiveStatus.Approved, ObjectiveStatus.MarkedAsDone].includes(
        status,
      )
    )
  }
  // For monetary targets whose currency differs from the one of the current contract of the employee
  const canDuplicateAndDismissOrDeleteObjective = () => {
    return (
      objective.targetAmount !== undefined &&
      objective.targetAmount !== null &&
      employeeCurrentContractCurrency !== objective.currency &&
      !employeeView &&
      !disabled &&
      [ObjectiveStatus.Proposal, ObjectiveStatus.ProposalFromManager, ObjectiveStatus.Approved, ObjectiveStatus.PastApproved].includes(
        status,
      )
    )
  }

  const isEditEnabled =
    (objective.targetAmount === undefined ||
      objective.targetAmount === null ||
      !objective.currency ||
      employeeCurrentContractCurrency === objective.currency) &&
    !disabled &&
    ([ObjectiveStatus.Proposal, ObjectiveStatus.ProposalFromManager, ObjectiveStatus.PastApproved].includes(status) ||
      ([ObjectiveStatus.Approved, ObjectiveStatus.MarkedAsDone].includes(status) && objective.id === null))

  const isViewEnabled =
    (objective.targetAmount === undefined ||
      objective.targetAmount === null ||
      employeeCurrentContractCurrency === objective.currency ||
      (employeeCurrentContractCurrency !== objective.currency &&
        [ObjectiveStatus.Dismissed, ObjectiveStatus.DismissedPending].includes(status)) ||
      employeeView) &&
    objective.id &&
    !disabled &&
    (status === ObjectiveStatus.Approved ||
      status === ObjectiveStatus.MarkedAsDone ||
      status === ObjectiveStatus.Dismissed ||
      status === ObjectiveStatus.DismissedPending)

  return (
    <>
      <PerTableActionButtons
        buttons={[
          {
            icon: 'view_fill',
            color: 'primary',
            tooltip: t('tooltip.view'),
            onClick: () => handleObjectiveClicked(!!isManagerPreparation, objective),
            showOnlyWhen: isViewEnabled,
            additionalAttrs: {
              'data-e2e-objective-button': 'view',
            },
            disabled: isButtonDisabled(),
          },
          {
            icon: 'edit_fill',
            color: 'primary',
            tooltip: t('tooltip.edit'),
            onClick: () => handleObjectiveClicked(!!isManagerPreparation, objective),
            showOnlyWhen: isEditEnabled,
            additionalAttrs: {
              'data-e2e-objective-button': 'edit',
            },
            disabled: isButtonDisabled(),
          },
          {
            icon: 'copy_fill',
            color: 'success',
            tooltip: t('tooltip.duplicate'),
            onClick: async () => {
              toggleDuplicating(true)
              await handleObjectiveDuplicate(objective.globalId!)
              toggleDuplicating(false)
            },
            showOnlyWhen: canDuplicateObjective(objective.status),
            additionalAttrs: {
              'data-e2e-objective-button': 'duplicate',
            },
            disabled: isButtonDisabled(),
          },
          {
            icon: 'dismiss_stroke',
            color: 'warning',
            tooltip: t('tooltip.dismiss'),
            onClick: async () => {
              toggleDismissing(true)
              await handleObjectiveDismiss(objective.id)
              toggleDismissing(false)
            },
            showOnlyWhen: canDismissObjective(objective.status),
            additionalAttrs: {
              'data-e2e-objective-button': 'dismiss',
            },
            disabled: isButtonDisabled(),
          },
          {
            icon: 'arrowRight_stroke',
            color: 'volcano',
            tooltip: !objective.id ? t('tooltip.duplicateAndDelete') : t('tooltip.duplicateAndDismiss'),
            onClick: () => {
              if (!!handleObjectiveDuplicateAndDismissOrDelete) handleObjectiveDuplicateAndDismissOrDelete(objective)
            },
            showOnlyWhen: canDuplicateAndDismissOrDeleteObjective(),
            additionalAttrs: {
              'data-e2e-objective-button': 'duplicateAndDismissOrDelete',
            },
            disabled: isButtonDisabled(),
          },
          {
            icon: 'delete_fill',
            color: 'error',
            tooltip: t('tooltip.delete'),
            onClick: () => {
              if (!!handleObjectiveDelete) handleObjectiveDelete(objective)
            },
            showOnlyWhen: canDeleteObjective(objective.status),
            additionalAttrs: {
              'data-e2e-objective-button': 'delete',
            },
            disabled: isButtonDisabled(),
          },
          {
            icon: 'redo_stroke',
            color: 'secondary',
            tooltip: t('tooltip.reactivate'),
            onClick: async () => {
              toggleReactivating(true)
              await handleObjectiveReactivate(objective.id)
              toggleReactivating(false)
            },
            showOnlyWhen: canReactivateObjective(objective.status),
            additionalAttrs: {
              'data-e2e-objective-button': 'reactivate',
            },
            disabled: isButtonDisabled(),
          },
        ]}
      />
    </>
  )
}
